import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { MapareRow } from '@lyra/shared';
import { wizardBucket, WIZARD_STEPS, type WizardStepId } from '../lib/wizard';
import { runProgress } from '../lib/progress';
import { WizardListStep } from './WizardListStep';
import { WizardReviewStep } from './WizardReviewStep';
import { WizardSummary } from './WizardSummary';

/** Ported verbatim (Rule Zero) from v1 `ValidationWizard.tsx`'s `STEP_DESCRIPTIONS`. */
const STEP_DESCRIPTIONS: Partial<Record<WizardStepId, string>> = {
  confirmat_rezolvat:
    'Clasificări automate cu încredere ridicată. Verificați lista, editați sau marcați incorect excepțiile, apoi validați-le în bloc.',
  ambiguu:
    'Articole care se potrivesc cu mai multe coduri MEDOC. Pentru fiecare: generați rezultate separate sau marcați nerezolvat.',
  revizuire: 'Discordanță între modele de clasificare. Alegeți varianta corectă pentru fiecare caz.',
  low_confidence:
    'Niciun candidat nu a depășit pragul de încredere. Mapați manual din nomenclator sau marcați nerezolvat.',
  no_match: 'Modelul nu a găsit niciun cod MEDOC corespunzător. Editați excepțiile, apoi validați-le ca «fără corespondent».',
  eroare: 'Rânduri cu erori de procesare. Reclasificați-le.',
};

export interface WizardShellProps {
  rows: MapareRow[];
  runId: number;
  finalized: boolean;
}

/**
 * Port of v1 `ValidationWizard.tsx` (Rule Zero) — left-nav of `WIZARD_STEPS`
 * (T1) with per-step remaining counts (`wizardBucket`, T1) and a green check
 * once a step's bucket is empty, a description band, auto-navigation to the
 * first non-empty step (guarded on `rows.length > 0` so a cold load doesn't
 * jump to „Sumar" before the real buckets are known), and a step router.
 * Retokenized to v2's tokens; laid out for v2's normal-flow scrolling page
 * (`AppShell`'s `<main>` scrolls the whole document) instead of v1's
 * fixed-viewport `flex-1 min-h-0` job-detail shell — nav + content sit
 * side-by-side and flow with the page, no internal viewport clipping.
 *
 * Step router: `list` mode (confirmat_rezolvat/no_match/eroare) renders
 * `WizardListStep` (T4 — bulk-select + validate-all-selected, per-row
 * edit/mark-incorrect; replaces T3's read-only `WizardListPlaceholder`);
 * `single`/`masterdetail` (ambiguu/revizuire/low_confidence) render
 * `WizardReviewStep`, which reuses `CorrectionPanel` per row for
 * `revizuire`/`low_confidence` and its own candidate-card + split view
 * (Task 5) for `ambiguu`; `summary` (sumar) renders `WizardSummary`.
 */
export function WizardShell({ rows, runId, finalized }: WizardShellProps) {
  const { step } = useParams<{ step?: string }>();
  const navigate = useNavigate();

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((r) => {
      const b = wizardBucket(r);
      c[b] = (c[b] ?? 0) + 1;
    });
    return c;
  }, [rows]);
  /** T4: same totals MaparePage's card opens with — passed through to the
   *  „sumar" step so its verdict sentence matches. */
  const progress = useMemo(() => runProgress(rows), [rows]);

  /** Rows in the „Confirmate/Rezolvate" bucket that were never validated (pipeline
   *  auto-confirmed, never human-signed-off) — the „validated but don't figure"
   *  rows the summary's „Validează tot ce a rămas" bulk-accepts so `validated`
   *  catches up to `classified`.
   *
   *  Gate on `wizardBucket === 'confirmat_rezolvat'`, NOT `rowHasFinalClassification`:
   *  the latter also matches a SPLIT CHILD (a `needs_review` row that already
   *  carries a candidate code), so bulk-accepting would validate BOTH conflicting
   *  children of one input as training gold — a C1-class poisoning. wizardBucket
   *  already excludes validated rows (they become 'done'). */
  const unvalidatedResolvedIds = useMemo(
    () => rows.filter((r) => wizardBucket(r) === 'confirmat_rezolvat').map((r) => r.id),
    [rows]
  );

  const firstNonEmpty =
    WIZARD_STEPS.find((s) => s.mode !== 'summary' && (counts[s.id] ?? 0) > 0)?.id ?? 'sumar';
  const current = (step as WizardStepId | undefined) ?? firstNonEmpty;
  const stepRows = useMemo(() => rows.filter((r) => wizardBucket(r) === current), [rows, current]);

  useEffect(() => {
    // Mirrors v1: wait for real rows before auto-selecting a step, so a cold
    // deep-load (rows briefly empty) doesn't redirect straight to „Sumar".
    if (!step && rows.length > 0) {
      navigate(`/mapari/${runId}/validare/${firstNonEmpty}`, { replace: true });
    }
  }, [step, firstNonEmpty, rows.length, runId, navigate]);

  if (!step) {
    return (
      <div className="mt-6 py-16 text-center text-[13.5px] text-ink-3">Se încarcă…</div>
    );
  }

  const def = WIZARD_STEPS.find((s) => s.id === current) ?? WIZARD_STEPS[WIZARD_STEPS.length - 1];

  return (
    <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-start">
      <nav aria-label="Pași de validare" className="w-full shrink-0 space-y-0.5 sm:w-56">
        {WIZARD_STEPS.map((s) => {
          const remaining = s.mode === 'summary' ? 0 : (counts[s.id] ?? 0);
          const done = s.mode !== 'summary' && remaining === 0;
          const active = s.id === current;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => navigate(`/mapari/${runId}/validare/${s.id}`)}
              aria-current={active ? 'step' : undefined}
              className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-[13.5px] transition-colors ${
                active ? 'bg-teal-wash font-medium text-teal' : 'text-ink-2 hover:bg-wash'
              }`}
            >
              <span>{s.label}</span>
              {s.mode !== 'summary' &&
                (done ? (
                  <Check className="size-4 shrink-0 text-ok" aria-hidden />
                ) : (
                  <span className="shrink-0 rounded-full bg-wash px-2 text-[11.5px] font-semibold tabular-nums text-ink-3">
                    {remaining}
                  </span>
                ))}
            </button>
          );
        })}
      </nav>

      <div className="min-w-0 flex-1 border-t border-rule-soft pt-4 sm:border-t-0 sm:border-l sm:pl-6 sm:pt-0">
        {STEP_DESCRIPTIONS[current] && (
          <div className="mb-4 border-b border-rule-soft pb-3">
            <h2 className="text-[13.5px] font-semibold text-ink">{def.label}</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-3">{STEP_DESCRIPTIONS[current]}</p>
          </div>
        )}

        {def.mode === 'list' && (
          <WizardListStep stepId={current} rows={stepRows} runId={runId} finalized={finalized} />
        )}
        {(def.mode === 'single' || def.mode === 'masterdetail') && (
          <WizardReviewStep stepId={current} rows={stepRows} runId={runId} finalized={finalized} />
        )}
        {def.mode === 'summary' && (
          <WizardSummary
            counts={counts}
            runId={runId}
            progress={progress}
            unvalidatedResolvedIds={unvalidatedResolvedIds}
            finalized={finalized}
          />
        )}
      </div>
    </div>
  );
}
